/**
 * O carrinho — revisar e enviar.
 *
 * A LINHA MOSTRA O DISPONÍVEL AO LADO DO PEDIDO, e avisa quando o pedido passa
 * dele. Não bloqueia: pedir mais do que tem é legítimo (o CD decide se compra
 * ou atende parcial — o fluxo de separação já trata isso). O que não pode é a
 * pessoa descobrir só depois; aqui ela decide sabendo.
 */

import { ErroApi, api } from '../../api.js';
import { escapar, numero } from '../../comum/formato.js';
import {
  obraAtual,
  carregarCarrinho,
  mudarQuantidade,
  mudarReserva,
  tirar,
  consumirNaoEncontrados,
  type Carrinho,
} from './estado.js';

export function montarCarrinho(
  raiz: HTMLElement,
  aoEsvaziar: () => void,
  /** Chamado quando a pessoa quer acompanhar o pedido recém-enviado. */
  aoAcompanhar?: () => void,
): void {
  const obra = obraAtual();
  if (!obra) {
    raiz.innerHTML = `<p class="aviso">Escolha a obra primeiro — o botão 🏗️ lá em cima.</p>`;
    return;
  }

  raiz.innerHTML = `<p class="carregando">Abrindo o carrinho…</p>`;
  void (async () => {
    try {
      desenhar(await carregarCarrinho(obra.codigo));
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir o carrinho.',
      )}</p>`;
    }
  })();

  function desenhar(c: Carrinho): void {
    if (c.itens.length === 0) {
      raiz.innerHTML = `
        <div class="vazio">
          <p>O carrinho de <b>${escapar(c.obraNome)}</b> está vazio.</p>
          <p class="dica">Adicione pela vitrine, ou importe uma lista inteira de uma vez.</p>
        </div>`;
      return;
    }

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🛒 Pedido para ${escapar(c.obraNome)}</h3>
        <div class="ped-carrinho">
          ${c.itens
            .map(
              (i) => `
            <div class="ped-clinha" data-linha="${escapar(i.codigo)}">
              <span class="ped-cfoto">${
                i.miniatura
                  ? `<img src="${i.miniatura}" alt="" ${i.foto ? `data-grande="${escapar(api.foto(i.foto) ?? '')}" data-fotos-de="${escapar(i.codigo)}"` : ''}>`
                  : i.foto
                    ? `<img src="${escapar(api.foto(i.foto) ?? '')}" alt="" loading="lazy" data-grande="${escapar(api.foto(i.foto) ?? '')}" data-fotos-de="${escapar(i.codigo)}">`
                    : '<span class="sem">—</span>'
              }</span>
              <span class="ped-cnome">
                <b>${escapar(i.nome)}</b>
                <span class="sub-inline">${escapar(i.codigo)} · disponível: ${numero(i.saldo)} ${escapar(i.unidade ?? '')}</span>
                ${
                  i.quantidade > i.saldo
                    ? `<span class="ped-alerta">pede ${numero(i.quantidade)}, tem ${numero(i.saldo)} — o CD decide se compra ou atende parcial</span>`
                    : ''
                }
                ${
                  // Ferramenta: o prazo de reserva é obrigatório — sem ele o
                  // envio volta. O campo mora aqui, na revisão do carrinho.
                  i.controlaUnidades
                    ? `<label class="ped-reserva ${i.reservadoAte ? '' : 'falta'}">
                         🔧 Reservar até
                         <input type="date" data-reserva value="${escapar(i.reservadoAte ?? '')}">
                       </label>`
                    : ''
                }
              </span>
              <span class="ped-cqtd">
                <input type="number" min="0.001" step="any" value="${i.quantidade}" data-qtd aria-label="Quantidade">
                <button class="ped-tirar" data-tirar title="Tirar do pedido">✕</button>
              </span>
            </div>`,
            )
            .join('')}
        </div>

        <label class="rotulado">
          <span>Observação (opcional)</span>
          <input id="ped-obs" maxlength="1000" placeholder="Ex.: precisa chegar antes de sexta">
        </label>

        <div class="conf-acoes">
          <button class="btn btn-grande" id="ped-enviar">Enviar pedido</button>
        </div>
        <p class="aviso" id="ped-erro" hidden></p>
      </section>`;

    ligar(c);
  }

  function ligar(c: Carrinho): void {
    const erro = raiz.querySelector<HTMLElement>('#ped-erro')!;

    raiz.querySelectorAll<HTMLElement>('[data-linha]').forEach((linha) => {
      const codigo = linha.dataset['linha']!;
      const campo = linha.querySelector<HTMLInputElement>('[data-qtd]')!;

      // Salva quando a pessoa TERMINA (change), não a cada tecla — cada
      // mudança é uma escrita no banco, e digitar "150" não são três pedidos.
      campo.addEventListener('change', () => {
        const q = Number(campo.value);
        if (!(q > 0)) {
          campo.value = '1';
          return;
        }
        void mudarQuantidade(obra!.codigo, codigo, q).then(desenhar).catch(mostrarErro);
      });

      linha.querySelector('[data-tirar]')!.addEventListener('click', () => {
        void tirar(obra!.codigo, codigo)
          .then((novo) => (novo.itens.length === 0 ? aoEsvaziar() : desenhar(novo)))
          .catch(mostrarErro);
      });

      linha.querySelector<HTMLInputElement>('[data-reserva]')?.addEventListener('change', (e) => {
        const data = (e.target as HTMLInputElement).value;
        if (!data) return;
        void mudarReserva(obra!.codigo, codigo, data).then(desenhar).catch(mostrarErro);
      });
    });

    raiz.querySelector('#ped-enviar')!.addEventListener('click', () => void enviar(c));

    function mostrarErro(e: unknown): void {
      erro.textContent = e instanceof ErroApi ? e.message : 'Não consegui salvar a mudança.';
      erro.hidden = false;
    }
  }

  async function enviar(c: Carrinho): Promise<void> {
    const botao = raiz.querySelector<HTMLButtonElement>('#ped-enviar')!;
    const erro = raiz.querySelector<HTMLElement>('#ped-erro')!;
    let obs = raiz.querySelector<HTMLInputElement>('#ped-obs')!.value.trim();

    // O que a lista importada não achou entra aqui, no envio — é assim que o
    // "reportar" da decisão do Lucas sobrevive à tela: o CD lê no pedido.
    const fora = consumirNaoEncontrados();
    if (fora.length) {
      const nota = `Vieram na lista e não temos no estoque: ${fora.join('; ')}`;
      obs = obs ? `${obs}\n${nota}` : nota;
      // O servidor recusa observação acima de 1000 — melhor cortar o fim da
      // lista do que o pedido inteiro voltar com erro na cara da pessoa.
      if (obs.length > 1000) obs = obs.slice(0, 997) + '…';
    }

    botao.disabled = true;
    botao.textContent = 'Enviando…';
    try {
      const r = await api.post<{ numero: string; itens: number }>(
        `/api/carrinho/${encodeURIComponent(obra!.codigo)}/envio`,
        obs ? { obs } : {},
      );
      raiz.innerHTML = `
        <div class="vazio">
          <p><b>Pedido ${escapar(r.numero)} enviado.</b></p>
          <p class="dica">${r.itens} item(ns) para ${escapar(c.obraNome)}. Assim que o CD abrir, você vê o status mudar.</p>
          ${aoAcompanhar ? '<button class="btn" id="ped-acompanhar">Acompanhar em Meus pedidos</button>' : ''}
        </div>`;
      raiz.querySelector('#ped-acompanhar')?.addEventListener('click', () => aoAcompanhar?.());
      // O carrinho desta obra deixou de existir (virou pedido); o contador zera
      // na próxima carga. Recarregar aqui criaria um carrinho novo à toa.
    } catch (e) {
      botao.disabled = false;
      botao.textContent = 'Enviar pedido';
      if (e instanceof ErroApi) {
        erro.innerHTML = `${escapar(e.message)}${
          e.detalhes?.length ? `<br>· ${e.detalhes.map(escapar).join('<br>· ')}` : ''
        }`;
      } else {
        erro.textContent = 'Não consegui enviar.';
      }
      erro.hidden = false;
    }
  }
}
